import React, { useRef, useState, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { PDFDocument } from "pdf-lib";

pdfjs.GlobalWorkerOptions.workerSrc =
  `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export default function PDFEditorWithDrawing() {
  const [file, setFile] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [mode, setMode] = useState("draw"); // draw | text
  const [typing, setTyping] = useState(false);
  const [typedText, setTypedText] = useState("");
  const [cursorPos, setCursorPos] = useState(null);

  // File Upload
  const handleFile = (e) => {
    const f = e.target.files[0];
    if (f && f.type === "application/pdf") setFile(URL.createObjectURL(f));
  };

  // 🖊️ Drawing Mode
  const startDraw = (e) => {
    if (mode !== "draw") return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    canvasRef.current.isDrawing = true;
    setDrawing(true);
  };

  const draw = (e) => {
    if (!canvasRef.current.isDrawing || mode !== "draw") return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    ctx.stroke();
  };

  const stopDraw = () => {
    canvasRef.current.isDrawing = false;
    setDrawing(false);
  };

  // 🔤 Text Typing Mode
  const handleCanvasClick = (e) => {
    if (mode !== "text") return;
    const rect = e.target.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setCursorPos({ x, y });
    setTypedText("");
    setTyping(true);
  };

  // Capture keyboard typing
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!typing || !cursorPos) return;

      const ctx = canvasRef.current.getContext("2d");
      ctx.font = "18px Arial";
      ctx.fillStyle = "blue";

      if (e.key === "Enter") {
        setTyping(false);
        setCursorPos(null);
        return;
      }

      if (e.key === "Backspace") {
        e.preventDefault();
        const newText = typedText.slice(0, -1);
        setTypedText(newText);
        redrawText(ctx, newText);
        return;
      }

      if (e.key.length === 1) {
        const newText = typedText + e.key;
        setTypedText(newText);
        redrawText(ctx, newText);
      }
    };

    const redrawText = (ctx, text) => {
      // clear the area to re-render text cleanly
      const { x, y } = cursorPos;
      ctx.clearRect(x, y - 20, 400, 25);
      ctx.fillText(text, x, y);
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [typing, typedText, cursorPos]);

  // Utility: Clear Canvas
  const clearCanvas = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };

  // 💾 Save PDF
  const savePDF = async () => {
    if (!file) return;
    const existingPdfBytes = await fetch(file).then((r) => r.arrayBuffer());
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const imgData = canvasRef.current.toDataURL("image/png");
    const pngImage = await pdfDoc.embedPng(imgData);
    const page = pdfDoc.getPage(0);
    const { width, height } = page.getSize();
    page.drawImage(pngImage, { x: 0, y: 0, width, height });
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "annotated.pdf";
    link.click();
  };

  return (
    <div style={{ textAlign: "center", fontFamily: "sans-serif" }}>
      <h2>📝 PDF Annotator ()</h2>
      <input type="file" accept="application/pdf" onChange={handleFile} />

      {file && (
        <>
          <div style={{ margin: "10px 0" }}>
            <button
              onClick={() => setMode("draw")}
              style={{
                background: mode === "draw" ? "#28a745" : "#007bff",
                color: "#fff",
                padding: "6px 12px",
                marginRight: "6px",
              }}
            >
              ✍️ Draw
            </button>
            <button
              onClick={() => setMode("text")}
              style={{
                background: mode === "text" ? "#28a745" : "#007bff",
                color: "#fff",
                padding: "6px 12px",
                marginRight: "6px",
              }}
            >
              🔤 Type
            </button>
            <button onClick={clearCanvas} style={{ marginRight: "6px" }}>
              🧹 Clear
            </button>
            <button onClick={savePDF}>💾 Save PDF</button>
          </div>

          <div style={{ position: "relative", display: "inline-block" }}>
            <Document
              file={file}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            >
              {Array.from(new Array(numPages), (_, i) => (
                <Page key={i} pageNumber={i + 1} width={600} />
              ))}
            </Document>

            <canvas
              ref={canvasRef}
              width={600}
              height={800}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                cursor: mode === "text" ? "text" : "crosshair",
              }}
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={stopDraw}
              onMouseLeave={stopDraw}
              onClick={handleCanvasClick}
            />
          </div>
        </>
      )}
    </div>
  );
}
